import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Label } from '../Form';
import { ApolloError, gql, useMutation } from '@apollo/client';
import TesterPicker from '../Tester/TesterPicker';
import useNotify from '../../hooks/useNotify';
import { useHistory } from 'react-router-dom';
import Form from '../Form/Form';
import FormColumn from '../Form/FormColumn';
import FormRow from '../Form/FormRow';
import { IdArrayInput } from '../../hooks/useTablePages';

const CREATE_BACKFLOW_ASSEMBLY_JOBS = gql`
  mutation CreateBackflowAssemblyJobs($input: BackflowAssemblyJobsInput!) {
    createBackflowAssemblyJobs(input: $input) {
      id
    }
  }
`;

export type OutboundAssignmentInput = {
  vendorProfileId: string | number;
  due?: number;
};

export type JobTypeKey =
  | 'BACKFLOW_TEST'
  | 'NEW_INSTALL'
  | 'DISCRETIONARY'
  | 'COMPLIANCE'
  | 'RESEARCH'
  | 'ADMINISTRATIVE'
  | 'RW_INSPECTION';

export type BackflowAssemblyJobsInput = {
  type: JobTypeKey;
  backflowAssemblyIds: IdArrayInput;
  outboundAssignment?: OutboundAssignmentInput;
};

type AddJobTracModalProps = {
  backflowAssemblyIds: IdArrayInput;
  onClose: (actionTaken: boolean) => void;
};

const AddAssemblyJobsModal: React.FC<AddJobTracModalProps> = ({
  backflowAssemblyIds,
  onClose,
}: AddJobTracModalProps) => {
  const [testerProfileId, setTesterProfileId] = useState<string>();
  const { showError } = useNotify();
  const history = useHistory();

  const [createJobs] = useMutation<
    { createBackflowAssemblyJobs: { id: string }[] },
    {
      input: BackflowAssemblyJobsInput;
    }
  >(CREATE_BACKFLOW_ASSEMBLY_JOBS);

  const save = async () => {
    try {
      if (backflowAssemblyIds.length) {
        const result = await createJobs({
          variables: {
            input: {
              type: 'BACKFLOW_TEST',
              backflowAssemblyIds,
              outboundAssignment: testerProfileId
                ? {
                    vendorProfileId: testerProfileId,
                  }
                : undefined,
            },
          },
        });
        if (result?.data?.createBackflowAssemblyJobs?.length) {
          history.push(
            `/company/jobs/${result?.data?.createBackflowAssemblyJobs[0].id}`
          );
        }
      }
      onClose(true);
    } catch (e) {
      showError('Error creating job', e as ApolloError);
    }
  };

  return (
    <Modal title="Create Backflow Tests" onClose={() => onClose(false)}>
      <ModalBody>
        <Form>
          <>
            <FormRow>
              <FormColumn>
                <Label>Assign to (Optional)</Label>
                <TesterPicker
                  testerProfileId={testerProfileId}
                  isClearable
                  where={{
                    status: ['ACTIVE', 'PENDING'],
                  }}
                  onChange={(tester) => setTesterProfileId(tester?.profileId)}
                />
              </FormColumn>
            </FormRow>
          </>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button icon="save" onClick={save}>
          Create{' '}
          {backflowAssemblyIds?.length > 1 ? backflowAssemblyIds?.length : ''}{' '}
          Job
          {backflowAssemblyIds?.length > 1 ? 's' : ''}
        </Button>
        <Button
          variant="outline"
          icon="slash-circle"
          onClick={() => onClose(false)}
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddAssemblyJobsModal.propTypes = {
  onClose: PropTypes.any,
};

export default AddAssemblyJobsModal;
